<template>
  <el-select
    :model-value="modelValue"
    filterable
    remote
    reserve-keyword
    clearable
    :remote-method="scheduleSearch"
    :loading="loading"
    :placeholder="placeholder"
    :disabled="disabled"
    :class="selectClass"
    :no-data-text="noDataText"
    popper-class="live-id-select-dropdown"
    @update:model-value="onUpdate"
    @visible-change="onVisible"
  >
    <el-option
      v-for="item in options"
      :key="item.userId"
      :label="labelOf(item)"
      :value="item.userId"
    >
      <div class="live-id-option">
        <span class="live-id-option__name">{{ item.nickname || item.realName || '主播' }}</span>
        <span class="live-id-option__id">用户 {{ item.userId }}</span>
      </div>
    </el-option>
  </el-select>
</template>

<script setup lang="ts">
import { ref, watch, onBeforeUnmount } from 'vue'
import { pageAnchor, type LiveAnchorVO } from '@/api/live/anchor'

const props = withDefaults(
  defineProps<{
    modelValue?: number | string | null
    /** 主播审核状态：默认仅已通过；传 undefined 表示不限 */
    status?: number
    placeholder?: string
    disabled?: boolean
    selectClass?: string
  }>(),
  {
    status: 1,
    placeholder: '输入昵称 / 姓名 / 编号搜索，点选下方列表',
    disabled: false,
    selectClass: '!w-240px'
  }
)

const emit = defineEmits<{
  (e: 'update:modelValue', value: number | undefined): void
  (e: 'change', value: number | undefined, anchor?: LiveAnchorVO): void
}>()

const loading = ref(false)
const options = ref<LiveAnchorVO[]>([])
const noDataText = ref('暂无数据，请输入关键字搜索')
let debounceTimer: ReturnType<typeof setTimeout> | undefined
let searchSeq = 0

const toNum = (v: number | string | null | undefined) => {
  if (v == null || v === '') return undefined
  const n = Number(v)
  return Number.isFinite(n) && n > 0 ? n : undefined
}

const labelOf = (item: LiveAnchorVO) => {
  const name = item.nickname || item.realName || '主播'
  return `${name}（用户 ${item.userId}）`
}

const ensureOption = async (userId?: number) => {
  if (!userId) return
  if (options.value.some((item) => item.userId === userId)) return
  try {
    const params: Record<string, any> = { pageNo: 1, pageSize: 1, userId }
    if (props.status != null) params.status = props.status
    const data: any = await pageAnchor(params)
    const one = (data.list || [])[0]
    if (one?.userId) {
      options.value = [one, ...options.value.filter((x) => x.userId !== one.userId)]
      return
    }
  } catch {
    /* fallback */
  }
  options.value = [
    { userId, nickname: `用户 ${userId}`, realName: '' } as LiveAnchorVO,
    ...options.value
  ]
}

const search = async (keyword?: string) => {
  const seq = ++searchSeq
  loading.value = true
  noDataText.value = '暂无数据，请输入关键字搜索'
  try {
    const text = String(keyword || '').trim()
    const params: Record<string, any> = { pageNo: 1, pageSize: 20 }
    if (props.status != null) params.status = props.status
    if (/^\d+$/.test(text)) {
      params.userId = Number(text)
    } else if (text) {
      params.realName = text
    }
    const data: any = await pageAnchor(params)
    let list: LiveAnchorVO[] = data.list || []
    if (/^\d+$/.test(text) && list.length === 0) {
      const fbParams: Record<string, any> = { pageNo: 1, pageSize: 20 }
      if (props.status != null) fbParams.status = props.status
      const fallback: any = await pageAnchor(fbParams)
      list = fallback.list || []
      noDataText.value = `未找到主播用户 ${text}，可从下列主播中选择或改用姓名搜索`
    }
    if (seq !== searchSeq) return
    options.value = list
  } finally {
    if (seq === searchSeq) loading.value = false
  }
}

const scheduleSearch = (keyword?: string) => {
  if (debounceTimer) clearTimeout(debounceTimer)
  debounceTimer = setTimeout(() => {
    void search(keyword)
  }, 200)
}

const onUpdate = (val: number | undefined) => {
  const id = toNum(val)
  emit('update:modelValue', id)
  const anchor = id == null ? undefined : options.value.find((item) => item.userId === id)
  emit('change', id, anchor)
}

const onVisible = async (open: boolean) => {
  if (open) await search('')
}

watch(
  () => props.modelValue,
  async (val) => {
    const id = toNum(val)
    if (id) await ensureOption(id)
  },
  { immediate: true }
)

onBeforeUnmount(() => {
  if (debounceTimer) clearTimeout(debounceTimer)
})

defineExpose({ search, ensureOption })
</script>

<style scoped>
.live-id-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
}
.live-id-option__name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.live-id-option__id {
  flex-shrink: 0;
  color: var(--el-text-color-secondary);
  font-size: 12px;
}
</style>
